import { apiFetch } from '../httpClient';

const UPCOMING_SCHEDULES_ENDPOINT =
  process.env.REACT_APP_UPCOMING_SCHEDULES_ENDPOINT || '/api/schedules/upcoming';
const MY_SCHEDULES_ENDPOINT =
  process.env.REACT_APP_MY_SCHEDULES_ENDPOINT || '/api/schedules/me';
const SCHEDULE_LIST_ENDPOINT =
  process.env.REACT_APP_SCHEDULE_LIST_ENDPOINT || '/api/schedules';
const SCHEDULE_CREATE_ENDPOINT =
  process.env.REACT_APP_SCHEDULE_CREATE_ENDPOINT || '/api/schedules';
const SCHEDULE_VOTE_ENDPOINT_TEMPLATE =
  process.env.REACT_APP_SCHEDULE_VOTE_ENDPOINT_TEMPLATE || '/api/teams/:teamId/schedules/:scheduleId/vote';
const SCHEDULE_UNVOTE_ENDPOINT_TEMPLATE =
  process.env.REACT_APP_SCHEDULE_UNVOTE_ENDPOINT_TEMPLATE || SCHEDULE_VOTE_ENDPOINT_TEMPLATE;
const SCHEDULE_UNVOTE_METHOD =
  process.env.REACT_APP_SCHEDULE_UNVOTE_METHOD || 'DELETE';
const SCHEDULE_VOTE_REMINDER_ENDPOINT_TEMPLATE =
  process.env.REACT_APP_SCHEDULE_VOTE_REMINDER_ENDPOINT_TEMPLATE ||
  '/api/teams/:teamId/schedules/:scheduleId/vote-reminder';
const SCHEDULE_ATTENDANCE_ENDPOINT_TEMPLATE =
  process.env.REACT_APP_SCHEDULE_ATTENDANCE_ENDPOINT_TEMPLATE ||
  '/api/teams/:teamId/schedules/:scheduleId/attendance';
const SCHEDULE_LINEUP_ENDPOINT_TEMPLATE =
  process.env.REACT_APP_SCHEDULE_LINEUP_ENDPOINT_TEMPLATE || '/api/teams/:teamId/schedules/:scheduleId/lineup';
const SCHEDULE_DETAIL_ENDPOINT_TEMPLATE =
  process.env.REACT_APP_SCHEDULE_DETAIL_ENDPOINT_TEMPLATE || '/api/teams/:teamId/schedules/:scheduleId';

function buildScheduleCreatePath(teamId) {
  if (!teamId) {
    return SCHEDULE_CREATE_ENDPOINT;
  }

  if (SCHEDULE_CREATE_ENDPOINT.includes(':teamId')) {
    return SCHEDULE_CREATE_ENDPOINT.replace(':teamId', encodeURIComponent(String(teamId)));
  }

  if (SCHEDULE_CREATE_ENDPOINT.includes('{teamId}')) {
    return SCHEDULE_CREATE_ENDPOINT.replace('{teamId}', encodeURIComponent(String(teamId)));
  }

  const hasQuery = SCHEDULE_CREATE_ENDPOINT.includes('?');
  return `${SCHEDULE_CREATE_ENDPOINT}${hasQuery ? '&' : '?'}teamId=${encodeURIComponent(String(teamId))}`;
}

// 템플릿 경로(:teamId, {teamId})를 실제 path param으로 치환한다.
function buildPathFromTemplate(template, pathParams = {}) {
  return Object.entries(pathParams).reduce((path, [key, value]) => {
    return path
      .replace(`:${key}`, encodeURIComponent(String(value)))
      .replace(`{${key}}`, encodeURIComponent(String(value)));
  }, template);
}

function buildScheduleVotePath(teamId, scheduleId) {
  return buildPathFromTemplate(SCHEDULE_VOTE_ENDPOINT_TEMPLATE, { teamId, scheduleId });
}

function buildScheduleLineupPath(teamId, scheduleId) {
  return buildPathFromTemplate(SCHEDULE_LINEUP_ENDPOINT_TEMPLATE, { teamId, scheduleId });
}

function buildScheduleVoteReminderPath(teamId, scheduleId) {
  return buildPathFromTemplate(SCHEDULE_VOTE_REMINDER_ENDPOINT_TEMPLATE, { teamId, scheduleId });
}

function buildScheduleAttendancePath(teamId, scheduleId) {
  return buildPathFromTemplate(SCHEDULE_ATTENDANCE_ENDPOINT_TEMPLATE, { teamId, scheduleId });
}

function buildScheduleDetailPath(teamId, scheduleId) {
  return buildPathFromTemplate(SCHEDULE_DETAIL_ENDPOINT_TEMPLATE, { teamId, scheduleId });
}

export async function fetchUpcomingSchedules({ page = 0, size = 8, teamId } = {}) {
  const searchParams = new URLSearchParams({
    page: String(page),
    size: String(size),
  });
  if (teamId) {
    searchParams.set('teamId', String(teamId));
  }

  return apiFetch(`${UPCOMING_SCHEDULES_ENDPOINT}?${searchParams.toString()}`, {
    method: 'GET',
  });
}

// 로그인한 사용자의 전체 팀 일정을 단일 API로 조회한다.
export async function fetchMySchedules({ page = 0, size = 20, from, to } = {}) {
  const searchParams = new URLSearchParams({
    page: String(page),
    size: String(size),
  });

  if (from) {
    searchParams.set('from', from);
  }
  if (to) {
    searchParams.set('to', to);
  }

  return apiFetch(`${MY_SCHEDULES_ENDPOINT}?${searchParams.toString()}`, {
    method: 'GET',
  });
}

// 팀의 전체 일정 목록을 조회한다(운영진 일정 관리 화면용).
export async function fetchTeamSchedules({ teamId }) {
  const searchParams = new URLSearchParams();
  if (teamId) {
    searchParams.set('teamId', String(teamId));
  }

  const query = searchParams.toString();
  const endpoint = query ? `${SCHEDULE_LIST_ENDPOINT}?${query}` : SCHEDULE_LIST_ENDPOINT;
  return apiFetch(endpoint, {
    method: 'GET',
  });
}

// 일정 등록 API. 팀 스코프가 쿼리/본문 어느 쪽이든 수용하도록 보낸다.
export async function createSchedule({ teamId, payload }) {
  const schedulePayload = {
    ...(payload || {}),
    ...(teamId ? { teamId: String(teamId) } : {}),
  };

  return apiFetch(buildScheduleCreatePath(teamId), {
    method: 'POST',
    body: schedulePayload,
  });
}

// 기존 일정을 수정한다.
export async function updateSchedule({ teamId, scheduleId, payload }) {
  return apiFetch(buildScheduleDetailPath(teamId, scheduleId), {
    method: 'PATCH',
    body: payload,
  });
}

// 기존 일정을 삭제한다.
export async function deleteSchedule({ teamId, scheduleId }) {
  return apiFetch(buildScheduleDetailPath(teamId, scheduleId), {
    method: 'DELETE',
  });
}

// 단일 일정 상세 정보를 조회한다.
export async function fetchScheduleDetail({ teamId, scheduleId }) {
  return apiFetch(buildScheduleDetailPath(teamId, scheduleId), {
    method: 'GET',
  });
}

// 일정 참여/불참 투표를 서버에 반영한다.
export async function voteSchedule({ teamId, scheduleId, voteType }) {
  return apiFetch(buildScheduleVotePath(teamId, scheduleId), {
    method: 'POST',
    body: { voteType },
  });
}

// 동일 버튼 재클릭 시 투표를 미참여 상태로 되돌린다.
export async function cancelScheduleVote({ teamId, scheduleId }) {
  return apiFetch(
    buildPathFromTemplate(SCHEDULE_UNVOTE_ENDPOINT_TEMPLATE, { teamId, scheduleId }),
    {
      method: SCHEDULE_UNVOTE_METHOD,
    },
  );
}

// 미응답자 대상 투표 독촉 알림을 발송한다.
export async function sendScheduleVoteReminder({ teamId, scheduleId }) {
  return apiFetch(buildScheduleVoteReminderPath(teamId, scheduleId), {
    method: 'POST',
  });
}

// 현재 기기 위치를 기준으로 일정 출석 체크를 수행한다.
export async function checkScheduleAttendance({
  teamId,
  scheduleId,
  latitude,
  longitude,
}) {
  return apiFetch(buildScheduleAttendancePath(teamId, scheduleId), {
    method: 'POST',
    body: {
      latitude,
      longitude,
    },
  });
}

// 일정별 출석 완료자 목록을 조회한다.
export async function fetchScheduleAttendances({ teamId, scheduleId }) {
  return apiFetch(buildScheduleAttendancePath(teamId, scheduleId), {
    method: 'GET',
  });
}

// 특정 경기 일정의 저장된 라인업을 조회한다.
export async function fetchScheduleLineup({ teamId, scheduleId }) {
  return apiFetch(buildScheduleLineupPath(teamId, scheduleId), {
    method: 'GET',
  });
}

// 쿼터별 라인업 편집 결과를 서버에 저장한다.
export async function saveScheduleLineup({ teamId, scheduleId, payload }) {
  return apiFetch(buildScheduleLineupPath(teamId, scheduleId), {
    method: 'PUT',
    body: payload,
  });
}
